<template>
    <div id="planbordRow" :style="{gridTemplateRows: workloadHeight + 'px 20px auto'}">
        <div class="planbord-table-holder" id="workload-holder">
            <PlanbordGridContainer v-if="roster" :informationColumns="informationColumns" :dayColumns="dayColumns" :hourBalanceColumns="enabledHourColumnCount" :compact="viewOptions.showCompactMode">
                <RosterWeeks :rosterViewSettings="rosterViewSettings" />
                <RosterComments :roster="roster" :permissions="permissions" :rosterViewSettings="rosterViewSettings" />
            </PlanbordGridContainer>
            <WorkloadRequirementsTable
                :roster="roster"
                :organisationDeploymentStates="organisation.organisationDeploymentStates"
                :organisationDeploymentTypes="organisation.organisationDeploymentTypes"
                :permissions="permissions[roster.rosterId]"
                :resourceFunctionNames="resourceFunctionNames"
                :informationColumns="informationColumns"
                :dayColumns="dayColumns"
                :hourBalanceColumns="enabledHourColumnCount"
                :compact="viewOptions.showCompactMode"
                v-if="viewOptions.useNewWorkloadRequirements"
            />
            <LegacyWorkloadTable
                :viewOptions="viewOptions"
                :roster="roster"
                :permissions="permissions[roster.rosterId]"
                :sorting="workloadRequirementSorting"
                :organisationDeploymentStates="organisation.organisationDeploymentStates"
                :shifts="shifts"
                :resourceFunctionNames="resourceFunctionNames"
                :informationColumns="informationColumns"
                :dayColumns="dayColumns"
                :hourBalanceColumns="enabledHourColumnCount"
                :compact="viewOptions.showCompactMode"
                v-else
            />
        </div>
        <BoardDivider @moveDivider="moveDivider" />
        <div class="planbord-table-holder" id="planbord-holder" @scroll="scrollPlanbord">
            <PlanbordGrid
                ref="grid"
                :viewOptions="viewOptions"
                :rosterNames="rosterNames"
                :roster="roster"
                :enabledCompanies="enabledCompanies"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :leaveOverviews="leaveOverviews"
                :deploymentCountConfigurations="deploymentCountConfigurations"
                :deploymentCounts="deploymentCounts"
                :enabledHourColumnCount="enabledHourColumnCount"
                :organisationDeploymentStates="organisation.organisationDeploymentStates"
                :contractDeviationLabels="contractDeviationLabels"
                :shifts="shifts"
                :permissions="permissions"
                :enabledWorkSchedule="enabledWorksSchedule"
                :informationColumns="informationColumns"
                :dayColumns="dayColumns"
                :hourBalanceColumns="enabledHourColumnCount"
                :workSchedulePositionsPerDay="workSchedulePositionsPerDay"
                @create:deployment="$emit('create:deployment', $event)"
                @update:deployment="$emit('update:deployment', $event)"
                @delete:deployment="$emit('delete:deployment', $event)"
                @create:stampShift="$emit('create:stampShift', $event)"
                @move:resourceRosterRank="$emit('move:resourceRosterRank', $event)"
                @move:rosterTitleRank="$emit('move:rosterTitleRank', $event)"
                @open:workSchedule="$emit('open:workSchedule', $event)"
                @open:addOpenShiftMarketplaceEmployee="$emit('open:addOpenShiftMarketplaceEmployee', $event)"
            />
            <div id="budget-divider">
                <div class="line"></div>
            </div>
            <template v-if="roster && roster.budgets && roster.budgets.length">
                <BudgetTable
                    v-for="budget in roster.budgets"
                    :key="budget.id"
                    :budget="budget"
                    :roster="roster"
                    :budgetDayCounters="budgetDayCounters"
                    :informationColumns="informationColumns"
                    :dayColumns="dayColumns"
                    :hourBalanceColumns="enabledHourColumnCount"
                    :compact="viewOptions.showCompactMode"
                />
            </template>
        </div>
    </div>
</template>

<script lang="ts">
    import LegacyWorkloadTable from '@/components/Planbord/LegacyWorkloadTable.vue';
    import WorkloadRequirementsTable from '@/components/Planbord/WorkloadRequirementsTable.vue';
    import BoardDivider from '@/components/Planbord/BoardDivider.vue';
    import PlanbordGrid from '@/components/Planbord/PlanbordGrid.vue';
    import BudgetTable from '@/components/Planbord/Budget/BudgetTable.vue';
    import {WorkloadRequirementSorting} from '@/types/enums/WorkloadRequirementSorting';
    import {ViewOptions, RosterData, BudgetDayCounters, PermissionsForRoster} from '@/types/types';
    import {BalanceFilterType} from '@/components/FilterBar/types';
    import {LeaveOverview, DeploymentCountConfiguration, Organisation, ContractCaptions, Shift} from '@cquential/api';
    import {DeploymentCounts} from '@/components/types';
    import {RosterViewSettings} from '@/types';
    import {CompanyWithDeploymentsAndEmployees, PlanbordWorkSchedulePosition} from '@/types/api-object-extensions';
    import PlanbordGridContainer from '@/components/Planbord/PlanbordGridContainer.vue';
    import RosterComments from '@/components/Planbord/RosterComments.vue';
    import RosterWeeks from '@/components/Planbord/RosterWeeks.vue';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            LegacyWorkloadTable,
            WorkloadRequirementsTable,
            BoardDivider,
            PlanbordGrid,
            BudgetTable,
            PlanbordGridContainer,
            RosterComments,
            RosterWeeks,
        },
        emits: [
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:stampShift',
            'move:resourceRosterRank',
            'move:rosterTitleRank',
            'open:workSchedule',
            'open:addOpenShiftMarketplaceEmployee',
        ],
    })
    // The name PlanbordRow revers to being a row in the main application grid. It is not a row of (e.g.) a resource, that's a PlanbordGridRow
    export default class PlanbordRow extends Vue {
        @Prop({required: true}) public viewOptions!: ViewOptions;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public enabledCompanies!: CompanyWithDeploymentsAndEmployees[];
        @Prop() public budgetDayCounters?: BudgetDayCounters;
        @Prop({required: true}) public preferences!: Record<string, string | number>;
        @Prop({required: true}) public enabledBalanceAndCountDisplays!: Record<BalanceFilterType, Record<number, boolean>>;
        @Prop({required: true}) public leaveOverviews!: LeaveOverview[];
        @Prop({required: true}) public deploymentCountConfigurations!: DeploymentCountConfiguration[];
        @Prop({required: true}) public deploymentCounts!: DeploymentCounts;
        @Prop({required: true}) public organisation!: Organisation;
        @Prop() public contractDeviationLabels?: ContractCaptions;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public permissions!: Record<number, PermissionsForRoster>;
        @Prop({type: Boolean}) public enabledWorksSchedule!: boolean;
        @Prop({required: true}) public resourceFunctionNames!: Record<number, string>;
        @Prop({required: true}) public rosterViewSettings!: RosterViewSettings;
        @Prop({type: Number, required: true}) public informationColumns!: number;
        @Prop({type: Number, required: true}) public dayColumns!: number;
        @Prop({required: true}) public workSchedulePositionsPerDay!: Record<string, PlanbordWorkSchedulePosition[]>;

        private readonly MIN_HEIGHT_WORKLOAD: number = 50;
        private readonly MIN_HEIGHT_REMAINING: number = 250;
        private workloadHeight: number = 200;

        public mounted(): void {
            window.addEventListener('resize', this.checkWorkloadHeight);
        }

        public beforeUnmount(): void {
            window.removeEventListener('resize', this.checkWorkloadHeight);
        }

        public scrollPlanbord(e: Event): void {
            document.getElementById('workload-holder')!.scrollLeft = (e.target! as Element).scrollLeft;
        }

        public moveDivider(offset: number): void {
            this.workloadHeight += offset;
            this.checkWorkloadHeight();
        }

        public checkWorkloadHeight(): void {
            if (this.workloadHeight > (window.innerHeight - this.MIN_HEIGHT_REMAINING)) {
                this.workloadHeight = window.innerHeight - this.MIN_HEIGHT_REMAINING;
            }
            if (this.workloadHeight < this.MIN_HEIGHT_WORKLOAD) {
                this.workloadHeight = this.MIN_HEIGHT_WORKLOAD;
            }
        }

        public get enabledHourColumnCount(): number {
            return Object.values(this.enabledBalanceAndCountDisplays)
                .map((displayEnabled: Record<number, boolean>): number => Object.values(displayEnabled).filter((enabled: boolean): boolean => enabled).length)
                .reduce((sum: number, currentValue: number): number => sum + currentValue, 0);
        }

        public get workloadRequirementSorting(): WorkloadRequirementSorting {
            return 'requirementSorting' in this.preferences ? this.preferences.requirementSorting as WorkloadRequirementSorting : WorkloadRequirementSorting.Alphabet;
        }
    }
</script>

<style lang="scss" scoped>
    #planbordRow {
        font-size: var(--planbord-font-size);
        overflow: hidden;
        display: grid;

        .planbord-table-holder {
            overflow: auto scroll;
        }

        #planbord-holder {
            padding-bottom: 200px;
        }

        #workload-holder {
            overflow-x: hidden;
        }
    }

    #budget-divider {
        --divider-background-color: #9c9c9c;
        margin: 0 15px;
        position: sticky;
        left: 0;

        .line {
            height: 11px;
            background: var(--divider-background-color);
            margin: 5px 0 4px 15px;
            border: 4px solid var(--main-background-color);
        }
    }
</style>
